/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Center,
  Flex,
  HStack,
  Skeleton,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBoolean,
} from '@chakra-ui/react';
import { useToastr } from '@laxmimanogna/code-quick-components';
import { formatDistanceStrict } from 'date-fns';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CQContainer from '../../components/layout/CQContainer';
import AppColors from '../../constants/AppColors';
import ROUTES from '../../constants/Routes';
import { FONT_FAMILY } from '../../constants/Theme';
import { withProvider } from '../../hoc/withProvider';
import NotificationProvider, {
  NotificationsContext,
} from '../../providers/NotificationProvider';
import { replaceRoute, toTitleCase } from '../../utils/common.utils';

const scrollStyle = {
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-track': {
    boxShadow: 'inset 0 0 4px lightGrey',
    width: '4px',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': { background: 'grey', borderRadius: '4px' },
};

function NotificationList({ readFilter }) {
  const notificationContext = useContext(NotificationsContext);
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const toast = useToastr();
  const [currentPage, setCurrentPage] = React.useState(0);
  const [isFetching, ifState] = useBoolean(false);

  const isUnreadTab = readFilter === false;
  const [showReadOnly, setShowReadOnly] = useState(false);
  const effectiveFilter = isUnreadTab ? false : (showReadOnly ? true : undefined);
  const perPage = 7;
  const lastPage = Math.ceil(notificationCount / perPage);

  const sortedByStatus = arr =>
    [...arr].sort((a, b) => Number(a.read) - Number(b.read) || new Date(b.created_at) - new Date(a.created_at));

  const getNotification = async () => {
    const response = await notificationContext.getNotifications(currentPage, effectiveFilter);
    setNotifications(prev => [...prev, ...response.results]);
    setNotificationCount(response.count);
    ifState.off();
  };

  useEffect(() => {
    setShowReadOnly(false);
    setNotifications([]);
    setNotificationCount(0);
    setCurrentPage(0);
  }, [readFilter]);

  useEffect(() => {
    setNotifications([]);
    setNotificationCount(0);
    setCurrentPage(0);
  }, [showReadOnly]);

  useEffect(() => {
    if (currentPage === 0) {
      setCurrentPage(1);
    } else {
      getNotification();
    }
  }, [currentPage]);

  const handleMarkSingleUnread = async (e, n) => {
    e.stopPropagation();
    await notificationContext.markUnread(n.notification_user_id);
    if (showReadOnly) {
      setNotifications(prev =>
        prev.filter(notif => notif.notification_user_id !== n.notification_user_id)
      );
    } else {
      setNotifications(prev =>
        sortedByStatus(prev.map(notif =>
          notif.notification_user_id === n.notification_user_id ? { ...notif, read: false } : notif
        ))
      );
    }
  };

  const handleMarkSingleRead = async (e, n) => {
    e.stopPropagation();
    if (!n.read) await notificationContext.markRead(n.notification_user_id);
    if (isUnreadTab) {
      setNotifications(prev =>
        prev.filter(notif => notif.notification_user_id !== n.notification_user_id)
      );
    } else {
      setNotifications(prev =>
        sortedByStatus(prev.map(notif =>
          notif.notification_user_id === n.notification_user_id ? { ...notif, read: true } : notif
        ))
      );
    }
  };

  const handleMarkAllRead = async () => {
    await notificationContext.markAllRead();
    if (isUnreadTab) {
      setNotifications([]);
      setNotificationCount(0);
      setCurrentPage(0);
    } else {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }
  };

  const loadNextData = async e => {
    const bottom =
      Math.abs(e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight) < 2;
    if (bottom && !notificationContext.isFetchingNotifications && currentPage < lastPage) {
      ifState.on();
      setCurrentPage(prev => prev + 1);
    }
  };

  const hasUnread = notifications.some(n => !n.read);

  return (
    <Box width={'40rem'}>
      <Flex justifyContent={'space-between'} alignItems={'center'} mb={2} minH={'28px'}>
        {!isUnreadTab && notificationContext.unReadNotification > 0 && (
          <Text
            fontSize={'sm'} fontWeight={'semibold'} cursor={'pointer'}
            color={showReadOnly ? AppColors.secondary : AppColors.darkGray}
            _hover={{ textDecoration: 'underline' }}
            onClick={() => setShowReadOnly(p => !p)}
          >
            {showReadOnly ? 'Show all' : 'Show read only'}
          </Text>
        )}
        {hasUnread && !showReadOnly && (
          <Text
            ml={'auto'} fontSize={'sm'} color={AppColors.secondary} cursor={'pointer'}
            fontWeight={'semibold'} _hover={{ textDecoration: 'underline' }}
            onClick={handleMarkAllRead}
          >
            Mark all as read
          </Text>
        )}
      </Flex>
      <Box
        onScroll={loadNextData} overflowY={'scroll'} sx={scrollStyle}
        maxHeight={'400px'} borderRadius={'md'} bg={AppColors.white} width={'100%'}
      >
        {notificationContext.isFetchingNotifications && !isFetching ? (
          <Stack bg={AppColors.bgColor}>
            <Skeleton height={'60px'} mb={1} />
            <Skeleton height={'60px'} mb={1} />
            <Skeleton height={'60px'} mb={1} />
          </Stack>
        ) : notifications?.length ? (
          notifications.map((n, i) => (
            <Box
              key={`${n.notification_user_id}-${i}`}
              width={'100%'}
              borderBottom={i !== notifications?.length - 1 && `1px solid ${AppColors.offWhite}`}
              borderLeft={!n.read ? `3px solid ${AppColors.secondary}` : '3px solid transparent'}
              display={'flex'} bg={!n.read ? AppColors.white : '#EBEBEE'} height={'60px'}
            >
              <Box
                flex={1} display={'flex'} alignItems={'center'}
                px={6} cursor={'pointer'} overflow={'hidden'}
                onClick={async () => {
                  if (!n.read) notificationContext.markRead(n.notification_user_id);
                  if (n.reference_chart) {
                    const res = await notificationContext.getAuditUpload(n.reference_chart);
                    if (res) {
                      navigate(replaceRoute(ROUTES.VIEW_AUDIT_SHEET, { uploadId: n.reference_chart }));
                    } else {
                      toast.showError({ description: `This chart has been deleted` });
                    }
                  }
                }}
              >
                <HStack flex={1} spacing={1} overflow={'hidden'} mr={3} alignItems={'center'}>
                  {n.message.startsWith('@') && (
                    <Text fontWeight={'semibold'} color={AppColors.secondary} whiteSpace={'nowrap'} fontSize={'sm'}>
                      {toTitleCase(`${n?.sender?.first_name} ${n?.sender?.last_name}`)}
                    </Text>
                  )}
                  <Text noOfLines={1} fontSize={'sm'}>
                    {n.message.startsWith('@') ? n.message?.substring(1) : n.message}
                  </Text>
                </HStack>
                <Text fontSize={'11px'} color={AppColors.darkGray} whiteSpace={'nowrap'} flexShrink={0}>
                  {formatDistanceStrict(new Date(n.created_at), new Date(), { addSuffix: true })}
                </Text>
              </Box>
              <Box
                role={'group'} w={'140px'} minW={'140px'}
                display={'flex'} alignItems={'center'} justifyContent={'center'}
                borderLeft={`1px solid ${AppColors.offWhite}`} cursor={'pointer'}
                onClick={e => { e.stopPropagation(); !n.read ? handleMarkSingleRead(e, n) : handleMarkSingleUnread(e, n); }}
              >
                <Box
                  px={3} py={'5px'} borderRadius={'full'}
                  border={`1px solid ${!n.read ? AppColors.secondary : AppColors.darkGray}`}
                  color={!n.read ? AppColors.secondary : AppColors.darkGray}
                  fontSize={'xs'} fontWeight={'semibold'}
                  _groupHover={{ bg: !n.read ? AppColors.secondary : AppColors.darkGray, color: 'white' }}
                  transition={'all 0.15s'} whiteSpace={'nowrap'}
                >
                  {!n.read ? 'Mark as read' : 'Mark as unread'}
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Center py={10} color={AppColors.darkGray}>No notifications</Center>
        )}
        {isFetching && <Center m={3}><Spinner /></Center>}
      </Box>
    </Box>
  );
}

function Notifications() {
  const notificationContext = useContext(NotificationsContext);

  return (
    <CQContainer justifyContent={'center'} display={'flex'}>
      <Box>
        <Text fontWeight={'semibold'} fontFamily={FONT_FAMILY.baiJamurjee} fontSize={'x-large'}>
          Notifications
        </Text>
        <Tabs mt={4} colorScheme={'blue'} isLazy>
          <TabList>
            <Tab fontWeight={'semibold'}>
              Unread
              {notificationContext.unReadNotification > 0 && (
                <Box
                  as={'span'} ml={2} px={2} py={'1px'} borderRadius={'full'}
                  bg={AppColors.danger} color={'white'} fontSize={'xs'}
                >
                  {notificationContext.unReadNotification}
                </Box>
              )}
            </Tab>
            <Tab fontWeight={'semibold'}>All</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}><NotificationList readFilter={false} /></TabPanel>
            <TabPanel px={0}><NotificationList readFilter={undefined} /></TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </CQContainer>
  );
}

export default withProvider(NotificationProvider, Notifications);
